import { useEffect, useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMediaResource } from '@brightmls/nextgen.lae.flc';
import {
  useMediaStore,
  type MediaResource,
  type PendingJob,
} from '@brightmls/nextgen.lae.mediacenter';
import {
  useMediaSubscription,
  type MediaJob,
} from '../../../hooks/useMediaSubscription';
import { useReso } from '../../../contexts/ResoContext';
import { useMetadata } from '../../../contexts/MetadataContext';
import { translateMediaRecord } from '../../../system/media';
import { MediaCategory } from '../../../system/media';
import { useMediaJobs } from '../../../contexts/MediaJobsContext';
import { useService } from '../../../contexts/ServiceContext';
import { useMediaV2Save } from './hooks/useMediaV2Save';
import { useMediaLogger } from './hooks/useMediaLogger';

export const MediaCenterDataLoader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { listingKey } = useParams<{ listingKey: string }>();
  const { media: flcMedia, updateMediaByCategory } = useMediaResource();
  const hydrate = useMediaStore((state) => state.hydrate);
  const hydratePendingJobs = useMediaStore((state) => state.hydratePendingJobs);
  const isHydrated = useMediaStore((state) => state.isHydrated);
  const { locale } = useMetadata();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const logger = useMediaLogger('MediaCenterDataLoader');

  // Get initial media jobs from the listing load (via MediaJobsContext)
  const { initialMediaJobs, initialMedia } = useMediaJobs();
  const hasHydratedJobsRef = useRef(false);

  // Media V2 save handler registration
  const { registerMediaV2SaveHandler } = useService();
  const { getDirtyMediaForSave } = useMediaV2Save();

  // Register the save handler with ServiceContext
  useEffect(() => {
    logger.log('Registering Media V2 save handler');
    registerMediaV2SaveHandler?.(getDirtyMediaForSave);

    return () => {
      // Clean up by registering a no-op handler
      registerMediaV2SaveHandler?.(() => []);
    };
  }, [registerMediaV2SaveHandler, getDirtyMediaForSave, logger]);

  const updateProcessingItem = useMediaStore(
    (state) => state.updateProcessingItem,
  );
  const updateItemKey = useMediaStore((state) => state.updateItemKey);
  const items = useMediaStore((state) => state.items);

  /**
   * Handle job completion - update item key and sync to FLC
   * Called when MEDIA_JOB_COMPLETE is received
   */
  const handleJobComplete = useCallback(
    (job: MediaJob) => {
      logger.log('Job complete:', {
        jobId: job.jobId,
        mediaKey: job.mediaKey,
        resultUrl: job.resultUrl,
        hasFinalMediaRecord: !!job.finalMediaRecord,
        aiStatus: job.aiStatus,
      });

      // Get AI description from the completed item in store (set during MEDIA_AI_UPDATE)
      const aiDescription =
        job.generateDescriptions &&
        job.aiResults &&
        job.aiResults.image_description?.description
          ? job.aiResults.image_description?.description
          : undefined;

      // Check if AI is still processing (scanning, pending, etc.)
      const aiStillProcessing =
        job.aiStatus &&
        !['completed', 'failed', 'skipped', 'scanned', 'applied'].includes(
          job.aiStatus,
        );

      // First, update the processing item with completion status
      // Keep _isProcessing true if AI is still scanning
      updateProcessingItem(job.jobId, {
        _status: 'completed',
        _aiStatus: job.aiStatus,
        _isProcessing: aiStillProcessing,
        _resultUrl: job.resultUrl,
        MEDIA_URL: job.resultUrl,
      } as any);

      // If we have a new mediaKey from the server, update the item key
      const newKey = job.finalMediaRecord?.MED_VW_KEY || job.mediaKey;
      if (newKey && newKey !== job.jobId) {
        logger.log('Updating item key:', {
          oldKey: job.jobId,
          newKey: newKey,
        });
        updateItemKey(
          job.jobId,
          newKey,
          job.finalMediaRecord?.MEDIA_KEY || newKey,
        );
      }

      // Sync to FLC using finalMediaRecord if available, otherwise build from job data
      let flcResource: any;

      if (job.finalMediaRecord) {
        // Use finalMediaRecord directly, but merge in AI description if available
        flcResource = {
          ...job.finalMediaRecord,
          // Merge AI description if AI descriptions was enabled and we have one
          ...(aiDescription && { MEDIA_SHORT_DESCRIPTION: aiDescription }),
        };
        logger.log('Using finalMediaRecord for FLC sync:', {
          key: flcResource.MED_VW_KEY,
          hasAiDescription: !!aiDescription,
        });
      } else {
        // Fallback: build from job data (for backwards compatibility)
        const completedItem = items.find((item) => item._jobId === job.jobId);
        if (!completedItem) {
          logger.warn('Could not find completed item for FLC sync');
          return;
        }

        flcResource = {
          MED_VW_KEY: job.mediaKey || job.jobId,
          MEDIA_KEY: job.mediaKey || job.jobId,
          MEDIA_CATEGORY: job.category,
          MEDIA_DISPLAY_ORDER:
            job.mediaDisplayOrder ?? completedItem.MEDIA_DISPLAY_ORDER,
          MEDIA_ITEM_NUMBER:
            job.mediaItemNumber ?? completedItem.MEDIA_ITEM_NUMBER,
          // Use AI description if available, otherwise use the item's description
          MEDIA_SHORT_DESCRIPTION:
            aiDescription ?? completedItem.MEDIA_SHORT_DESCRIPTION,
          MEDIA_LONG_DESCRIPTION: completedItem.MEDIA_LONG_DESCRIPTION,
          MEDIA_URL: job.resultUrl,
          MEDIA_VISIBILITY: job.visibility ?? completedItem.MEDIA_VISIBILITY,
          MEDIA_IMAGE_WIDTH:
            job.imageMetadata?.width ?? completedItem.MEDIA_IMAGE_WIDTH,
          MEDIA_IMAGE_HEIGHT:
            job.imageMetadata?.height ?? completedItem.MEDIA_IMAGE_HEIGHT,
          MEDIA_MODIFICATION_TIMESTAMP:
            job.updatedAt || new Date().toISOString(),
        };
      }

      // Determine the category for FLC update
      const category = job.finalMediaRecord?.MEDIA_CATEGORY || job.category;
      let mediaCategory: MediaCategory | undefined;
      if (category === 'Photo') {
        mediaCategory = MediaCategory.Photo;
      } else if (category === 'Document') {
        mediaCategory = MediaCategory.Document;
      } else if (category === 'Floor Plan') {
        mediaCategory = MediaCategory.FloorPlan;
      }

      if (mediaCategory && flcResource) {
        // Get current FLC media for this category
        const currentFlcMedia = (flcMedia as any[]).filter(
          (m) => m.MEDIA_CATEGORY === category,
        );

        // Add the new resource (or update if key already exists)
        const existingIndex = currentFlcMedia.findIndex(
          (m) => m.MED_VW_KEY === flcResource.MED_VW_KEY,
        );

        let updatedMedia;
        if (existingIndex >= 0) {
          updatedMedia = [...currentFlcMedia];
          updatedMedia[existingIndex] = flcResource;
        } else {
          updatedMedia = [...currentFlcMedia, flcResource];
        }

        logger.log('Syncing completed item to FLC:', {
          category: mediaCategory,
          itemCount: updatedMedia.length,
          key: flcResource.MED_VW_KEY,
        });

        updateMediaByCategory({
          category: mediaCategory,
          resources: updatedMedia,
        });
      }
    },
    [
      updateProcessingItem,
      updateItemKey,
      items,
      flcMedia,
      updateMediaByCategory,
      logger,
    ],
  );

  /**
   * Handle WebSocket job updates
   * Maps MediaJob fields to MediaItem fields for store update
   */
  const handleJobUpdate = useCallback(
    (job: MediaJob) => {
      logger.log('Job update received:', {
        jobId: job.jobId,
        status: job.status,
        aiStatus: job.aiStatus,
        failedStep: job.failedStep,
        mediaKey: job.mediaKey,
        isAIError: job.isAIError,
      });

      // Check if item is already in a terminal failed state
      // Don't let AI updates or other non-failed updates overwrite a failed submission
      const currentItem = items.find(
        (item) => item.MED_VW_KEY === job.jobId || item._jobId === job.jobId,
      );
      if (currentItem?._isFailed && job.status !== 'failed') {
        logger.log('Ignoring update for already failed item:', {
          jobId: job.jobId,
          currentStatus: 'failed',
          incomingStatus: job.status,
        });
        return;
      }

      // Extract AI description if available and AI descriptions were requested
      let aiDescription: string | undefined;
      if (
        job.generateDescriptions &&
        job.aiStatus === 'scanned' &&
        job.aiResults?.image_description?.description
      ) {
        aiDescription = job.aiResults.image_description.description;
        logger.log('AI description received:', aiDescription);
      }

      // Determine if still processing:
      // - Job is not in terminal state (completed/failed)
      // - OR AI is still scanning (even if job is submitted)
      const jobStillProcessing = !['completed', 'failed'].includes(job.status);
      const aiStillProcessing =
        job.aiStatus &&
        !['completed', 'failed', 'skipped', 'scanned', 'applied'].includes(
          job.aiStatus,
        );
      const isProcessing = jobStillProcessing || aiStillProcessing;

      updateProcessingItem(job.jobId, {
        MED_VW_KEY: job.jobId,
        MEDIA_CATEGORY: job.category,
        MEDIA_SOURCE_FILE_NAME: job.fileName,
        MEDIA_DISPLAY_ORDER: job.mediaDisplayOrder,
        MEDIA_ITEM_NUMBER: job.mediaItemNumber,
        MEDIA_VISIBILITY: job.visibility,
        // Update SHORT_DESCRIPTION from AI if available
        ...(aiDescription && { MEDIA_SHORT_DESCRIPTION: aiDescription }),
        // Job status fields
        _status: job.status,
        _aiStatus: job.aiStatus,
        _isProcessing: isProcessing,
        _isFailed: job.status === 'failed',
        _failedStep: job.failedStep,
        _progress: job.progress,
        _previewUrl: job.previewUrl,
        _processedUrl: job.processedUrl,
        _resultUrl: job.resultUrl,
        _errorMessage: job.error,
        _canRetry: job.canRetry,
        _retryCount: job.retryCount,
        _aiRetryCount: job.aiRetryCount,
        // AI and metadata
        _aiResults: job.aiResults,
        _imageMetadata: job.imageMetadata,
        _pdfMetadata: job.pdfMetadata,
        MEDIA_MODIFICATION_TIMESTAMP: job.updatedAt || job.createdAt,
      } as any);
    },
    [updateProcessingItem, items, logger],
  );

  /**
   * Route job updates to appropriate handlers
   */
  const handleJobMessage = useCallback(
    (job: MediaJob) => {
      // Route to completion handler for completed jobs
      if (job.status === 'completed') {
        handleJobComplete(job);
      } else {
        // All other updates go to the regular handler
        handleJobUpdate(job);
      }
    },
    [handleJobComplete, handleJobUpdate],
  );

  /**
   * Subscribe to media updates via WebSocket
   * This updates the connectionId for all jobs in the listing and sets up broadcast listeners
   */
  useMediaSubscription({
    listingKey: listingKey || '',
    enabled: !!listingKey,
    reconcile: true, // Full reconciliation on mount to sync any in-progress jobs
    onJobUpdate: handleJobMessage,
    onError: (error) => {
      logger.error('Subscription error:', error);
    },
  });

  /**
   * Hydrate MediaCenter store from FLC media
   */
  useEffect(() => {
    if (!isHydrated) {
      hydrate(initialMedia); // TODO: fix the type mismatch. they are identical (nearly)
    }
  }, [initialMedia, hydrate, isHydrated]);

  /**
   * Hydrate initial media jobs from get-listing response.
   * These are in-progress/processing jobs that should show immediately.
   */
  useEffect(() => {
    if (
      isHydrated &&
      initialMediaJobs.length > 0 &&
      !hasHydratedJobsRef.current
    ) {
      hasHydratedJobsRef.current = true;

      logger.log(`Hydrating ${initialMediaJobs.length} initial media jobs`);

      // Convert MediaJobs to PendingJob format expected by the store
      const pendingJobs: PendingJob[] = initialMediaJobs.map(
        (job) =>
          ({
            jobId: job.jobId,
            fileName: job.fileName,
            fileSize: job.fileSize,
            category: job.category,
            status: job.status,
            previewUrl: job.previewUrl,
            processedUrl: job.processedUrl,
            resultUrl: job.resultUrl,
            error: job.error,
            imageMetadata: job.imageMetadata,
            pdfMetadata: job.pdfMetadata,
            createdAt: job.createdAt,
            updatedAt: job.updatedAt,
          }) as any,
      );

      logger.log('Pending jobs:', pendingJobs);

      hydratePendingJobs(pendingJobs);
    }
  }, [isHydrated, initialMediaJobs, hydratePendingJobs]);

  // Show loading state until hydration completes
  if (!isHydrated) {
    return <div>Loading media center...</div>;
  }

  return <>{children}</>;
};
